"use client";
import type { RowData } from "@tanstack/react-table";

import { useRouter } from "next/navigation";
import { isRecordObjectType } from "@shared/records/object-types";
import {
  renderPresentation,
  type PresentationRenderers,
} from "./presentation-renderers";
import {
  hasActiveRecordFilters,
  resolveRecordPresentation,
  shouldShowRecordControls,
  shouldShowRecordHeader,
} from "./record-table-model";
import { canPersistViewsFor, openRecordWith } from "./record-table-support";
import { useAllViews } from "./state/use-all-views";
import { useRecordTableRows } from "./use-record-table-rows";
import { useViewActions } from "./views/use-view-actions";
import {
  ALL_VIEW_ID,
  type RecordPresentationMode,
  type RecordTableProps,
  type RecordTotal,
} from "./types";

function useRecordViews<TData extends RowData>(
  { object, state, views = [], canPersistViews }: RecordTableProps<TData>,
  total: RecordTotal,
) {
  const savedViewObjectType = isRecordObjectType(object.id)
    ? object.id
    : undefined;
  const actions = useViewActions(savedViewObjectType);
  const hasFilters = hasActiveRecordFilters(state);
  return {
    actions,
    canPersistViews: canPersistViewsFor(
      object,
      savedViewObjectType,
      canPersistViews,
    ),
    hasFilters,
    allViews: useAllViews(object, views, hasFilters ? undefined : total),
    activeViewId: state.activeViewId ?? ALL_VIEW_ID,
  };
}

function usePresentationContent<TData extends RowData>(
  { object, state, data }: RecordTableProps<TData>,
  mode: RecordPresentationMode,
  renderers: PresentationRenderers<TData>,
  rows: TData[],
) {
  const router = useRouter();
  return renderPresentation(mode, renderers, {
    rows,
    object,
    state,
    isLoading: data.isLoading ?? false,
    openRecord: openRecordWith(object, (href) => router.push(href)),
  });
}

export function useRecordTableModel<TData extends RowData>(
  props: RecordTableProps<TData>,
) {
  const { object, state, data, infiniteScroll = false } = props;
  const renderers = {
    board: props.renderBoard,
    calendar: props.renderCalendar,
    map: props.renderMap,
  };
  const presentation = resolveRecordPresentation(
    object,
    state.presentationMode,
    renderers,
  );
  const recordRows = useRecordTableRows(
    data,
    state,
    infiniteScroll,
    presentation.mode,
  );
  const recordViews = useRecordViews(props, recordRows.total);
  return {
    ...recordRows,
    ...recordViews,
    infiniteScroll,
    presentation,
    presentationContent: usePresentationContent(
      props,
      presentation.mode,
      renderers,
      recordRows.rows,
    ),
    selectable:
      object.capabilities.selection && props.bulkActions !== undefined,
    showHeader: shouldShowRecordHeader(props),
    showControls: shouldShowRecordControls(
      props,
      presentation.showSwitcher,
      recordViews.canPersistViews,
    ),
  };
}

export type RecordTableModel<TData extends RowData> = ReturnType<
  typeof useRecordTableModel<TData>
>;
